import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
    fetchAvailableBoards,
    toggleBoardSelection,
    setSearchQuery,
    updateBoardContext,
    optimisticUpdateBoardInContext,
    optimisticAddBoardToContext,
    optimisticRemoveBoardFromContext
} from "../../redux/Slices/boardContextSlice";
import { useSocketAction } from "../../hooks/useSocketManager";
import { ActionTypes } from "../../sockets/ActionTypes";

import './scss/BoardContextList.scss';
import Spinner from "../Global/Spinner";

const BoardContextList = () => {
    const dispatch = useDispatch();
    const searchInputRef = useRef(null);

    const {
        selectedBoards,
        availableBoards,
        availableBoardsLoading,
        searchQuery
    } = useSelector(state => state.boardContext);

    const { user } = useSelector((state) => state.user);

    // Load available boards on mount only if we don't have them already
    useEffect(() => {
        if (availableBoards.length === 0 && !searchQuery) {
            dispatch(fetchAvailableBoards({ search: '', limit: 20 }));
        }
    }, [dispatch, availableBoards.length]);

    // Real-time socket listener for board changes
    useSocketAction([
        ActionTypes.BOARD_CREATED,
        ActionTypes.BOARD_UPDATED,
        ActionTypes.BOARD_DELETED,
        ActionTypes.MEMBER_ADDED,
        ActionTypes.MEMBER_REMOVED,
    ], (data) => {
        // Skip events triggered by current user (already handled optimistically)
        if (data.reqUser === user?.user?._id) return;

        console.log('[BoardContextList] Real-time board event:', data.action, data);

        switch (data.action) {
            case ActionTypes.BOARD_CREATED:
                // ✅ Add board to context using optimistic action
                if (data.board) {
                    dispatch(optimisticAddBoardToContext(data.board));
                }
                break;

            case ActionTypes.BOARD_UPDATED:
                // ✅ Update board in context using optimistic action
                if (data.boardId && (data.board || data.updatedFields)) {
                    const updates = data.board || data.updatedFields;
                    dispatch(optimisticUpdateBoardInContext({
                        boardId: data.boardId,
                        updates
                    }));
                }
                break;

            case ActionTypes.BOARD_DELETED:
                // ✅ Remove board from context using optimistic action
                if (data.boardId) {
                    dispatch(optimisticRemoveBoardFromContext(data.boardId));
                }
                break;

            case ActionTypes.MEMBER_ADDED:
                // ✅ Board member added - if current user was added, show the board
                if (data.boardId && data.board) {
                    const isCurrentUserAdded = data.newMembers?.some(
                        nm => nm.user.toString() === user?.user?._id
                    );

                    if (isCurrentUserAdded) {
                        // Add the board to available boards
                        dispatch(optimisticAddBoardToContext(data.board));
                    } else {
                        // Update board members in existing board
                        dispatch(optimisticUpdateBoardInContext({
                            boardId: data.boardId,
                            updates: { members: data.members }
                        }));
                    }
                }
                break;

            case ActionTypes.MEMBER_REMOVED:
                // ✅ Board member removed - if current user was removed, hide the board
                if (data.boardId && data.memberId) {
                    if (data.memberId === user?.user?._id) {
                        // Remove the board from available boards
                        dispatch(optimisticRemoveBoardFromContext(data.boardId));
                    } else {
                        // Update board members in existing board
                        dispatch(optimisticUpdateBoardInContext({
                            boardId: data.boardId,
                            updates: { members: data.members }
                        }));
                    }
                }
                break;

            default:
                break;
        }
    }, [dispatch, user?.user?._id]);

    // Fetch boards when search query changes (debounced)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            dispatch(fetchAvailableBoards({ search: searchQuery, limit: 20 }));
        }, searchQuery ? 300 : 0);

        return () => clearTimeout(timeoutId);
    }, [searchQuery, dispatch]);

    // Auto-focus search input when component mounts
    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);

    // Handle search input change
    const handleSearchChange = (e) => {
        dispatch(setSearchQuery(e.target.value));
    };

    // Handle board selection toggle - immediately save to database
    const handleBoardToggle = (board) => {
        // First update local state optimistically
        dispatch(toggleBoardSelection(board));

        // Then update database
        const isCurrentlySelected = selectedBoards.some(b => b.shortId === board.shortId);
        let updatedBoards;

        if (isCurrentlySelected) {
            // Remove from selection
            updatedBoards = selectedBoards.filter(b => b.shortId !== board.shortId);
        } else {
            // Add to selection
            updatedBoards = [...selectedBoards, board];
        }

        // Save to database immediately
        dispatch(updateBoardContext(updatedBoards));
    };

    // Check if board is selected
    const isBoardSelected = (board) => {
        return selectedBoards.some(b => b.shortId === board.shortId);
    };



    return (
        <div className="board-list">
            <h2>Boards Context</h2>
            <div className="search-boards">
                <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search boards..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="search-input"
                />
            </div>
            <ul>
                {(availableBoardsLoading && availableBoards.length === 0) || (availableBoardsLoading && searchQuery) ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                        <Spinner size={30} color="#3498db" speed={1.5} />
                    </div>
                ) : availableBoards?.length > 0 ? (
                    availableBoards.map((board, index) => (
                        <li key={index} className="board-item">
                            <div
                                className={`board-link ${isBoardSelected(board) ? 'selected' : ''}`}
                                onClick={() => handleBoardToggle(board)}
                            >
                                <div className="board-title">{board.title}</div>
                                {isBoardSelected(board) && (
                                    <span className="material-symbols-outlined check-icon">
                                        check
                                    </span>
                                )}
                            </div>
                        </li>
                    ))
                ) : (
                    // Show message if no boards are found
                    <p>{searchQuery ? `No boards found for "${searchQuery}"` : 'No boards available.'}</p>
                )}
            </ul>

        </div>
    );
};

export default BoardContextList;
